import { RefObject } from 'react';

const getDistance = (a: Touch, b: Touch) => {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
};

const getMidpoint = (a: Touch, b: Touch) => ({
  x: (a.clientX + b.clientX) / 2,
  y: (a.clientY + b.clientY) / 2,
});

const createApplyPinchTransform =
  (imageRef: RefObject<HTMLImageElement>) => (scale: number, originX: number, originY: number) => {
    const img = imageRef.current;

    if (!img) {
      return;
    }

    img.style.transition = 'transform 0s';
    img.style.transformOrigin = `${originX}px ${originY}px`;
    img.style.transform = `scale(${scale})`;
    img.style.cursor = scale > 1 ? 'zoom-out' : 'zoom-in';
  };

const createResetPinchTransform = (imageRef: RefObject<HTMLImageElement>) => () => {
  const img = imageRef.current;

  if (!img) {
    return;
  }

  const cleanup = () => {
    if (img) {
      img.style.transformOrigin = '';
      img.style.transition = '';
      img.removeEventListener('transitionend', cleanup);
    }
  };
  img.addEventListener('transitionend', cleanup, { once: true });

  img.style.transition = 'transform 200ms ease-out';
  img.style.transform = 'scale(1)';
  img.style.cursor = 'zoom-in';
};

export const createTouchHandlers = (
  imageRef: RefObject<HTMLImageElement>,
  pinchState: React.MutableRefObject<{
    initialDistance: number;
    initialScale: number;
    originX: number;
    originY: number;
    active: boolean;
  }>,
) => {
  const applyPinchTransform = createApplyPinchTransform(imageRef);
  const resetPinchTransform = createResetPinchTransform(imageRef);

  const handleTouchStart = (e: TouchEvent) => {
    const img = imageRef.current;

    if (e.touches.length !== 2 || !img) {
      return;
    }

    const [t1, t2] = [e.touches[0], e.touches[1]];
    pinchState.current.initialDistance = getDistance(t1, t2);
    pinchState.current.initialScale = 1;
    const mid = getMidpoint(t1, t2);

    const rect = img.getBoundingClientRect();
    pinchState.current.originX = mid.x - rect.left;
    pinchState.current.originY = mid.y - rect.top;
    pinchState.current.active = true;
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!pinchState.current.active || e.touches.length !== 2) {
      return;
    }

    const [t1, t2] = [e.touches[0], e.touches[1]];
    const distance = getDistance(t1, t2);
    const scale = Math.max(
      1,
      (distance / pinchState.current.initialDistance) * pinchState.current.initialScale,
    );

    applyPinchTransform(scale, pinchState.current.originX, pinchState.current.originY);
    e.preventDefault();
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (!pinchState.current.active) {
      return;
    }

    if (e.touches.length < 2) {
      pinchState.current.active = false;
      resetPinchTransform();
    }
  };

  return { handleTouchStart, handleTouchMove, handleTouchEnd };
};
